import React, { useState, useEffect } from "react";
import styles from "./FiltersPanel.module.css";
import {
  BlueButton,
  FindmorePanel,
  WhiteButton,
} from "@react-solutions-ui/findmore-ui";
import Dropdown from "../../../features/employeeTeams/components/Filters/Dropdown";
import SwapIcon from "../../../assets/icons/swapRightIcon.svg";

import { DatePicker, ConfigProvider } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import "dayjs/locale/pt";
import ptPT from "antd/locale/pt_PT";
import { Locale } from "antd/es/locale";

dayjs.locale("pt");

const customAntLocale: Locale = {
  ...ptPT,
  DatePicker: {
    ...ptPT.DatePicker,
    lang: {
      ...ptPT.DatePicker?.lang,
      monthFormat: "MMM",
      shortMonths: [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez",
      ],
    },
  } as any,
};

// Types
interface Filters {
  author: string;
  category: string;
  year: string;
  startDate: string | null;
  endDate: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: Filters) => void;
  onClear: () => void;
  activeFilters: Filters;
  authors: string[];
  categories: string[];
  years: string[];
  dates: string[];
}

export default function FiltersPanel({
  isOpen,
  onClose,
  onApply,
  onClear,
  authors,
  categories,
  years,
  activeFilters,
  dates,
}: Props) {
  const [author, setAuthor] = useState<string>(
    activeFilters?.author ?? "Todos"
  );
  const [category, setCategory] = useState<string>(
    activeFilters?.category ?? "Todas"
  );
  const [year, setYear] = useState<string>(activeFilters?.year ?? "Todos");
  const [startDate, setStartDate] = useState<string | null>(
    activeFilters?.startDate ?? null
  );
  const [endDate, setEndDate] = useState<string | null>(
    activeFilters?.endDate ?? null
  );

  useEffect(() => {
    setAuthor(activeFilters?.author ?? "Todos");
    setCategory(activeFilters?.category ?? "Todas");
    setYear(activeFilters?.year ?? "Todos");
    setStartDate(activeFilters?.startDate ?? null);
    setEndDate(activeFilters?.endDate ?? null);
  }, [activeFilters]);

  const applyFilters = () => {
    onApply({
      author,
      category,
      year,
      startDate,
      endDate,
    });
    onClose();
  };

  const clearAll = () => {
    setAuthor("Todos");
    setCategory("Todas");
    setYear("Todos");
    setStartDate(null);
    setEndDate(null);
    onClear();
  };

  if (!isOpen) return null;

  const firstDate = dates.sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  )[0];

  const lastDate = dates.sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  )[0];

  const minDate = dayjs(firstDate);
  const maxDate = dayjs(lastDate);

  return (
    <FindmorePanel open={isOpen} onClose={onClose} title="FILTROS">
      <div className={styles.panelContent}>
        <div className={styles.content}>
          <div className={styles.fieldGroup}>
            <Dropdown
              label="Autor da nota"
              options={["Todos", ...authors.filter((a) => a !== "Todos")]}
              selectedValue={author}
              onSelect={setAuthor}
            />
          </div>

          <div className={styles.fieldGroup}>
            <Dropdown
              label="Categorias"
              options={["Todas", ...categories.filter((c) => c !== "Todas")]}
              selectedValue={category}
              onSelect={setCategory}
            />
          </div>

          <div className={styles.fieldGroup}>
            <Dropdown
              label="Ano"
              options={["Todos", ...years.filter((y) => y !== "Todos")]}
              selectedValue={year}
              onSelect={setYear}
            />
          </div>

          <div className={styles.dateRow}>
            <div className={styles.fieldGroup}>
              <div className={styles.label}>Data de início</div>
              <ConfigProvider locale={customAntLocale}>
                <DatePicker
                  className={styles.inputSize}
                  placeholder="Data de início"
                  format="YYYY-MM-DD"
                  showNow={false}
                  value={startDate ? dayjs(startDate) : minDate}
                  minDate={minDate}
                  maxDate={endDate ? dayjs(endDate) : maxDate}
                  onChange={(date: Dayjs | null) => {
                    const newStartDate = date
                      ? date.format("YYYY-MM-DD")
                      : null;
                    setStartDate(newStartDate);

                    if (endDate && newStartDate && newStartDate > endDate) {
                      setEndDate(newStartDate);
                    }
                  }}
                />
              </ConfigProvider>
            </div>

            <img src={SwapIcon} alt="Swap Icon" className={styles.swapIcon} />

            <div className={styles.fieldGroup}>
              <div className={styles.label}>Data de fim</div>
              <ConfigProvider locale={customAntLocale}>
                <DatePicker
                  className={styles.inputSize}
                  placeholder="Data de fim"
                  format="YYYY-MM-DD"
                  showNow={false}
                  value={endDate ? dayjs(endDate) : maxDate}
                  minDate={startDate ? dayjs(startDate) : minDate}
                  maxDate={maxDate}
                  onChange={(date: Dayjs | null) => {
                    const newEndDate = date ? date.format("YYYY-MM-DD") : null;
                    setEndDate(newEndDate);

                    if (startDate && newEndDate && newEndDate < startDate) {
                      setStartDate(newEndDate);
                    }
                  }}
                />
              </ConfigProvider>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <div className={styles.bottomButtons}>
            <WhiteButton text="LIMPAR FILTROS" onClick={clearAll} />
            <BlueButton text="APLICAR" onClick={applyFilters} />
          </div>
        </div>
      </div>
    </FindmorePanel>
  );
}
